import { Injectable, inject } from '@angular/core';
import {NetworkService} from '../../../../../../projects/common/src/lib/services/network.service';
import {BaseApiService} from '../base-api.service';
import {FirebaseApi} from '../../firebase-api.service';
import {Logger} from '../../../utils/logger';
import {ProjectComment} from '../../../model/project-comment';
import {AuthService} from '../../auth.service';
import {ProjectData} from '../../../model/project-data';
import {ProductData} from '../../../model/product-data';

/** GET /projects/:id/access. */
export interface ProjectAccessAnswer {
  access: 'edit' | 'view' | 'none';
  /** none: not_found | private | owner_plan_ended; view: trashed | own_plan_ended | org_viewer | shared | embed_pass; edit: why. */
  reason: string;
}


@Injectable({
  providedIn: 'root',
})
export class ProjectApiService extends BaseApiService {
  private authService = inject(AuthService);


  constructor() {

    super();
  }

  createPublicNewProject(data: {
    name: string,
    description: string,
    access_level: string,
    parentId: string
  }) {
    return this.postPublic<any>(`/projects/new`, data);
  }

  /**
   * Makes a project and answers it as stored, with the id the server gave it.
   * The server records it in the activity log. `centre` is where the map is:
   * a project started inside a team's territory is put on that team, and
   * `assignedTeam` names it.
   */
  createNewProject(data: {
    name: string,
    description: string,
    access_level: string,
    parentId: string,
    default_product_id?: string,
    centre?: {lat: number, lng: number}
  }) {
    return this.post<ProjectData & {assignedTeam?: {id: string, name: string}}>(`/projects/new`, data);
  }

  /**
   * Puts a project on one of the organisation's teams, or takes it off. The
   * server adds or removes the team's people and records it. Answers the
   * project's teams as they now are.
   */
  setProjectTeam(projectId: string, teamId: string, assigned: boolean) {
    return this.put<{teamIds: string[], teams: {id: string, name: string, color?: string}[]}>(
      `/projects/${encodeURIComponent(projectId)}/teams`, {teamId, assigned});
  }

  createNewFolder(data: {
    name: string,
    description: string,
    parentId: string
  }) {
    return this.post<any>(`/folders/new`, data);
  }

  getProjects(page: number, pageLimit: number, shared: boolean = false) {
    return this.get(`/projects?page=${page}&limit=${pageLimit}&shared=${shared}`);
  }

  getProjectData(projectId: string) {
    return this.get(`/projects/${projectId}/data`);
  }

  /**
   * The price list a project is quoted with, for someone who can open the
   * project but not read the catalogue itself -- a visitor of a shared link,
   * an Open Guest lead, an embed. Internal fields (cost price) are left out.
   */
  getProjectProducts(projectId: string): Promise<ProductData[]> {
    return this.get<ProductData[]>(`/projects/${encodeURIComponent(projectId)}/products`);
  }

  /**
   * May this session open the project, and how (`edit`, `view`, `none`), and
   * why: the server's one answer for every app, decided as the Firestore
   * rules decide. `owner_plan_ended`: its owner's plan has ended, so nobody
   * else opens it (owner's rule, 2026-11-05).
   */
  getProjectAccess(projectId: string): Promise<ProjectAccessAnswer> {
    return this.get<ProjectAccessAnswer>(`/projects/${encodeURIComponent(projectId)}/access`);
  }

  /** The projects shared with this account that it can no longer open (their owner's plan has ended), and whose. */
  getUnavailableSharedProjects(): Promise<{projects: string[], owners: string[]}> {
    return this.get<{projects: string[], owners: string[]}>('/projects/shared/unavailable');
  }

  setProjectData(projectId: string, data, merge: boolean) {
    return this.put(`/projects/${projectId}/data?merge=${merge}`, data);
  }

  updateProjectInfo(projectId: string, data) {
    return this.put(`/projects/${projectId}`, data);
  }

  /** Renames a folder (`name`, `description`); the server records the rename. */
  updateFolderInfo(folderId: string, data: {name?: string, description?: string}) {
    return this.put(`/projects/folders/${encodeURIComponent(folderId)}`, data);
  }

  /**
   * Changes how a project is shared. The server checks who may, checks the
   * Open Guest plan, and records an access change in the activity log.
   */
  updateProjectSharing(projectId: string, data: {access_level?: string, comments_enabled?: boolean}) {
    return this.put<{access_level: string, comments_enabled: boolean}>(
      `/projects/${encodeURIComponent(projectId)}/sharing`, data);
  }

  getProjectMmp(pid: string) {
    return this.get(`/projects/${pid}/mmp`);
  }

  shareFolder(folderId: string, emails: string[]) {
    return this.put<any>(`/projects/folders/${folderId}/members`, {emails});
  }

  /**
   * Copies a project on the server, files included. `withGeometry: false`
   * copies only its settings and map layers (no longer used by the app:
   * templates copy everything, as Duplicate does).
   */
  duplicateProject(projectId: string, data: {name: string, description: string,
                                             withGeometry?: boolean, asTemplate?: boolean,
                                             parentId?: string}) {
    return this.post<any>(`projects/${projectId}/copy`, data);
  }

  /** Moves a project onto (or off) the templates shelf. */
  setProjectTemplate(projectId: string, isTemplate: boolean) {
    return this.put<any>(`projects/${projectId}/template`, {isTemplate});
  }

  joinProjects(data: {projectIds: string[], parentId: string, name: string}) {
    return this.post<any>(`projects/join`, data);
  }

  moveProject(projectId: string, toParentId: string, path: string) {
    return this.post<any>(`projects/${projectId}/move`, {parentId: toParentId, path});
  }

  moveFolder(folderId: string, toParentId: string, path: string) {
    return this.post<any>(`projects/folders/${folderId}/move`, {parentId: toParentId, path});
  }

  shareProject(projectId: string, emails: string[]) {
    return this.put<any>(`projects/${projectId}/members`, {emails});
  }

  removeMemberFromFolder(folderId: string, uid: string) {
    return this.delete(`projects/folders/${folderId}/members`, {uids: [uid]});
  }

  removeMemberFromProject(projectId: string, uid: string) {
    return this.delete(`projects/${projectId}/members`, {uids: [uid]});
  }


  /**
   * Move a project to the trash (or restore it).
   *
   * Deleting is otherwise immediate and irreversible, which is a harsh
   * outcome for a mis-click on a survey that took hours to measure.
   */
  trashProject(projectId: string, trashed: boolean = true) {
    return this.post<any>(`projects/${projectId}/trash`, {trashed});
  }

  /** Permanent removal — no way back. */
  deleteProject(projectId: string) {
    return this.delete(`projects/${projectId}`, {});
  }

  /**
   * Deletes a folder. For its creator, their projects in it go to the trash;
   * `trashed` says how many. Anyone else only leaves it.
   */
  deleteFolder(fid: string) {
    return this.delete<{trashed: number}>(`projects/folders/${fid}`, {});
  }

  /**
   * Leave a comment on a project.
   *
   * Guests used to go a second way, sending a fixed string as the
   * Authorization header because they had no account. That string was
   * compiled into this bundle, so it was public, and the route accepted it
   * as proof of identity -- which made commenting on any project id an
   * unauthenticated write.
   *
   * There is only one path now. A guest is signed in anonymously with
   * Firebase before they ever reach a project, so they already carry a real
   * token; the server checks it and then checks that the project is one they
   * are allowed to see.
   */
  createComment(pid: string, comment: ProjectComment) {
    return this.post(`projects/${pid}/comments`, comment);
  }

  /** Removes a comment, into the project's comment trash: restoreComments brings it back. */
  deleteComment(pid: string, commentId: string) {
    return this.delete(`/projects/${pid}/comments/${commentId}`, null);
  }

  /** Clear Map with Comments ticked: the ids actually removed. */
  clearComments(pid: string, ids: string[]) {
    return this.post<{cleared: string[]}>(`/projects/${pid}/comments/clear`, {ids});
  }

  /** Undo: puts removed comments back as they were. */
  restoreComments(pid: string, ids: string[]) {
    return this.post<{restored: string[]}>(`/projects/${pid}/comments/restore`, {ids});
  }

  transferProjects(fromUid: string, toUid: string): Promise<ProjectData[]> {
    return this.post(`projects/transfer`, {fromUid, toUid});
  }

  recalculateProjectsBounds(){
    return this.post(`projects/update/bounds`, {});
  }
}
